import { Component, OnDestroy, OnInit } from '@angular/core';
import { Store } from '@ngrx/store';
import { Subject, takeUntil } from 'rxjs';
import { getCurrentProject } from '@completion/selectors';
import { ReportService } from '@completion/services';
import { RfccStatusReport } from '@completion/models';

@Component({
  selector: 'app-rfcc-status-report',
  templateUrl: './rfcc-status-report.component.html',
  styleUrls: ['./rfcc-status-report.component.scss'],
  standalone: false,
})
export class RfccStatusReportComponent implements OnInit, OnDestroy {
  reportData: RfccStatusReport[] = [];
  cpGroups: { cpNumber: string; rows: RfccStatusReport[] }[] = [];
  visibleCpGroups: { cpNumber: string; rows: RfccStatusReport[] }[] = [];
  filterStatus: 'ALL' | 'COMPLETE' | 'PENDING_SIGNATURES' | 'PARTIAL' | 'NO_RFCC' = 'ALL';
  statusCounts = { complete: 0, signaturesPending: 0, partial: 0, noRfcc: 0 };
  sortBy: 'cpNumber' | 'coverageStatus' | 'signatureCompletion' = 'cpNumber';
  sortDescending = false;
  loading = false;
  error = false;
  remindingRfccId: number | null = null;
  reminderSent = new Set<number>();
  private readonly destroy$ = new Subject<void>();

  constructor(
    private readonly store: Store,
    private readonly reportService: ReportService,
  ) {}

  ngOnInit(): void {
    this.store
      .select(getCurrentProject)
      .pipe(takeUntil(this.destroy$))
      .subscribe((project) => {
        if (project?.id) {
          this.load(project.id);
        }
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  load(projectId: number): void {
    this.loading = true;
    this.error = false;
    this.reportService
      .getRfccStatusReport(projectId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (report) => {
          this.reportData = report;
          this.buildGroups();
          this.loading = false;
        },
        error: () => {
          this.reportData = [];
          this.loading = false;
          this.error = true;
        },
      });
  }

  buildGroups(): void {
    const grouped = new Map<string, RfccStatusReport[]>();
    this.reportData.forEach((row) => {
      const rows = grouped.get(row.cpNumber) || [];
      rows.push(row);
      grouped.set(row.cpNumber, rows);
    });
    this.cpGroups = Array.from(grouped, ([cpNumber, rows]) => ({ cpNumber, rows }));
    this.calculateStatusCounts();
    this.sortGroups();
    this.applyFilter();
  }

  sortGroups(): void {
    const direction = this.sortDescending ? -1 : 1;
    this.cpGroups.sort((left, right) => {
      if (this.sortBy === 'cpNumber') {
        return left.cpNumber.localeCompare(right.cpNumber, undefined, { numeric: true }) * direction;
      }
      const leftValue = this.groupSortValue(left);
      const rightValue = this.groupSortValue(right);
      return (leftValue - rightValue) * direction;
    });
    this.applyFilter();
  }

  applyFilter(): void {
    if (this.filterStatus === 'PENDING_SIGNATURES') {
      this.visibleCpGroups = this.cpGroups
        .map((group) => ({ ...group, rows: group.rows.filter((row) => !!row.rfccId && !row.allSignaturesSigned) }))
        .filter((group) => group.rows.length > 0);
    } else if (this.filterStatus === 'ALL') {
      this.visibleCpGroups = this.cpGroups;
    } else {
      const targetClass = this.filterStatus === 'COMPLETE' ? 'cp-complete' : this.filterStatus === 'PARTIAL' ? 'cp-partial' : 'cp-no-rfcc';
      this.visibleCpGroups = this.cpGroups.filter((group) => this.cpClass(group.rows) === targetClass);
    }
  }

  setFilter(filterStatus: 'ALL' | 'COMPLETE' | 'PENDING_SIGNATURES' | 'PARTIAL' | 'NO_RFCC'): void {
    this.filterStatus = filterStatus;
    this.applyFilter();
  }

  isFilterActive(filterStatus: 'ALL' | 'COMPLETE' | 'PENDING_SIGNATURES' | 'PARTIAL' | 'NO_RFCC'): boolean {
    return this.filterStatus === filterStatus;
  }

  calculateStatusCounts(): void {
    this.statusCounts = { complete: 0, signaturesPending: 0, partial: 0, noRfcc: 0 };
    this.cpGroups.forEach((group) => {
      const status = this.cpClass(group.rows);
      if (status === 'cp-complete') this.statusCounts.complete++;
      else if (status === 'cp-signatures-pending') this.statusCounts.signaturesPending++;
      else if (status === 'cp-no-rfcc') this.statusCounts.noRfcc++;
      else this.statusCounts.partial++;
    });
  }

  toggleSortDirection(): void {
    this.sortDescending = !this.sortDescending;
    this.sortGroups();
  }

  private groupSortValue(group: { cpNumber: string; rows: RfccStatusReport[] }): number {
    if (this.sortBy === 'coverageStatus') {
      return group.rows.every((row) => row.coverageStatus === 'FULL') ? 1 : 0;
    }
    return (
      group.rows.reduce((total, row) => total + (row.signatureCount ? row.signedSignatureCount / row.signatureCount : 0), 0) /
      group.rows.length
    );
  }

  rowClass(row: RfccStatusReport): string {
    if (row.coverageStatus === 'NO_RFCC') {
      return 'rfcc-no-certificate';
    }
    if (row.coverageStatus !== 'FULL') {
      return 'rfcc-partial';
    }
    return row.allSignaturesSigned ? 'rfcc-complete' : 'rfcc-signatures-pending';
  }

  coverageSeverity(row: RfccStatusReport): 'success' | 'info' | 'warn' | 'secondary' {
    if (row.coverageStatus === 'NO_RFCC') {
      return 'secondary';
    }
    if (row.coverageStatus !== 'FULL') {
      return 'warn';
    }
    return row.allSignaturesSigned ? 'success' : 'info';
  }

  cpClass(rows: RfccStatusReport[]): string {
    if (rows.every((row) => row.coverageStatus === 'NO_RFCC')) {
      return 'cp-no-rfcc';
    }
    if (rows.every((row) => row.coverageStatus === 'FULL' && row.allSignaturesSigned)) {
      return 'cp-complete';
    }
    return rows.every((row) => row.coverageStatus === 'FULL') ? 'cp-signatures-pending' : 'cp-partial';
  }

  sendReminder(row: RfccStatusReport): void {
    if (!row.rfccId || row.allSignaturesSigned || this.remindingRfccId !== null) {
      return;
    }
    this.remindingRfccId = row.rfccId;
    this.reportService
      .sendRfccReminder(row.cpId, row.rfccId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.reminderSent.add(row.rfccId);
          this.remindingRfccId = null;
        },
        error: () => {
          this.remindingRfccId = null;
          this.error = true;
        },
      });
  }
}
